import { useState, useEffect, useMemo } from "react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import JSON5 from "json5";
import { ChevronRight } from "lucide-react";
import { ToolStatusIcon } from "~/components/assistant-v2/ToolStatusIcon";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DetailField, type InstructionsContent } from "~/components/assistant/messages/DetailField";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  python_analyzer: "Python Analyzer",
  data_retriever: "Data Retriever",
  biomcp_fda: "BioMCP FDA",
  get_dataset_metadata: "Get Dataset Metadata",
  execute_sql: "Execute SQL",
  final_result: "Final Result",
};

function getToolDisplayName(toolName: string): string {
  return (
    TOOL_DISPLAY_NAMES[toolName] ??
    toolName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function tryParseJSON(str: string): string | object {
  try {
    return JSON.parse(str);
  } catch {
    try {
      return JSON5.parse(str);
    } catch {
      return str;
    }
  }
}

function unwrap(obj: Record<string, any>): string | object {
  if (obj && typeof obj.raw === "string" && Object.keys(obj).length === 1) {
    return tryParseJSON(obj.raw);
  }
  return obj;
}

interface ParsedArgs {
  instructions: string | null;
  rest: object | null;
}

function parseArgs(unwrapped: string | object): ParsedArgs {
  if (
    typeof unwrapped === "object" &&
    unwrapped !== null &&
    !Array.isArray(unwrapped) &&
    "instructions" in unwrapped &&
    typeof (unwrapped as { instructions: unknown }).instructions === "string"
  ) {
    const { instructions, ...rest } = unwrapped as { instructions: string; [key: string]: unknown };
    return {
      instructions,
      rest: Object.keys(rest).length > 0 ? rest : null,
    };
  }
  return { instructions: null, rest: null };
}

function hasOutput(result: unknown): boolean {
  if (result == null) {
    return false;
  }
  if (typeof result === "string") {
    return result.trim().length > 0;
  }
  if (typeof result === "object") {
    return Object.keys(result).length > 0;
  }
  return true;
}

export const DefaultToolMessage: ToolCallMessagePartComponent = (props) => {
  const { toolName, argsText, result, status } = props;

  const isCancelled = status?.type === "incomplete" && status.reason === "cancelled";

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (status?.type === "running") {
      setIsExpanded(true);
    } else if (status?.type === "complete") {
      setIsExpanded(false);
    }
  }, [status?.type]);

  const parsedArgs = useMemo(() => {
    if (!argsText) {
      return null;
    }
    try {
      return JSON.parse(argsText);
    } catch {
      return null;
    }
  }, [argsText]);

  const unwrappedArgs = useMemo(() => {
    if (!parsedArgs) {
      return argsText ?? "";
    }
    return unwrap(parsedArgs);
  }, [parsedArgs, argsText]);

  const { instructions, rest } = useMemo(() => parseArgs(unwrappedArgs), [unwrappedArgs]);

  const inputContent: string | object | InstructionsContent = instructions
    ? rest
      ? { __kind: "instructions" as const, text: instructions, rest }
      : instructions
    : unwrappedArgs;

  const unwrappedResult = useMemo(() => {
    if (result == null) {
      return null;
    }
    if (typeof result === "string") {
      return tryParseJSON(result);
    }
    if (typeof result === "object" && "raw" in result) {
      return unwrap(result as Record<string, any>);
    }
    return result;
  }, [result]);

  const hasArgs = argsText && argsText !== "{}";
  const displayName = getToolDisplayName(toolName);
  const showOutput = hasOutput(result);

  if (!hasArgs) {
    return (
      <div className="border border-gray-200 rounded-lg p-2 mb-2">
        <div className="flex gap-2 items-center">
          <ToolStatusIcon status={status} />
          <span className="text-xs font-medium text-purple-600">
            {displayName}
            {isCancelled && " (cancelled)"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-2 mb-2">
      <details
        className="space-y-3"
        open={isExpanded}
        onToggle={(e) => {
          const target = e.target as HTMLDetailsElement;
          setIsExpanded(target.open);
          e.stopPropagation();
        }}
      >
        <summary className="list-none cursor-pointer mb-0 select-none">
          <div className="flex items-center gap-1.5 justify-between">
            <div className="flex gap-2 items-center">
              <ToolStatusIcon status={status} />
              <span className="text-xs font-medium text-purple-600">
                {displayName}
                {isCancelled && " (cancelled)"}
              </span>
            </div>
            <ChevronRight
              className={cn(
                "size-4 text-purple-600 transition-transform duration-200",
                isExpanded && "rotate-90"
              )}
            />
          </div>
        </summary>

        <div className="p-1 pt-2">
          {!showOutput ? (
            <div className="group relative pt-1">
              <CopyButton
                text={JSON.stringify(parsedArgs ?? argsText, null, 2)}
                variant="light"
                size="sm"
                className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
              />
              <DetailField content={inputContent} />
            </div>
          ) : (
            <Tabs defaultValue="output" className="gap-0">
              <TabsList variant="underlined" className="mb-2">
                <TabsTrigger
                  value="output"
                  className="text-xs py-1.5 pb-2 mr-4 data-[state=active]:text-foreground data-[state=active]:after:bg-foreground"
                >
                  Output
                </TabsTrigger>
                <TabsTrigger
                  value="input"
                  className="text-xs py-1.5 pb-2 data-[state=active]:text-foreground data-[state=active]:after:bg-foreground"
                >
                  Input
                </TabsTrigger>
              </TabsList>

              <TabsContent value="output">
                <div className="group relative">
                  <CopyButton
                    text={typeof result === "string" ? result : JSON.stringify(result, null, 2)}
                    variant="light"
                    size="sm"
                    className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  <DetailField content={unwrappedResult ?? ""} />
                </div>
              </TabsContent>

              <TabsContent value="input">
                <div className="group relative">
                  <CopyButton
                    text={JSON.stringify(parsedArgs ?? argsText, null, 2)}
                    variant="light"
                    size="sm"
                    className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  <DetailField content={inputContent} />
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </details>
    </div>
  );
};
